import React, { FC, useMemo } from 'react';

import { distinctValues } from 'Policies/utils';
import { FeeStrategyCard } from './FeeStrategyCard';
import styles from './styles.module.css';
import { FeeCharge, FeeStrategy } from './types';
import { chargeValue } from './utils';

export type FeeStrategyProps = {
  feeStrategies: Array<FeeStrategy>;
  selectedStrategy: string;
  onStrategySelect: (strategyName: string) => void;
  onLearnMore?: () => void;
  disabled?: boolean;
};

export const FeeStrategyPicker: FC<FeeStrategyProps> = ({
  feeStrategies,
  selectedStrategy,
  onStrategySelect,
  onLearnMore,
  disabled,
}) => {
  const allFees = useMemo(() => {
    const feeMap = new Map<
      string,
      { name: string; displayName: string; maxCharge: FeeCharge }
    >();
    feeStrategies
      .flatMap((strategy) => strategy.fees)
      .forEach((fee) => {
        if (!feeMap.has(fee.name)) {
          feeMap.set(fee.name, {
            name: fee.name,
            displayName: fee.displayName,
            maxCharge: fee.charge,
          });
        } else {
          const existing = feeMap.get(fee.name)!;
          const existingChargeValue = chargeValue(existing.maxCharge);
          const newChargeValue = chargeValue(fee.charge);
          existing.maxCharge =
            existingChargeValue > newChargeValue
              ? existing.maxCharge
              : fee.charge;
        }
      });
    return Array.from(feeMap.values());
  }, [feeStrategies]);

  const allFeatures = useMemo(() => {
    // Config uses feeStrategies[].features for two different things: customer-facing features to
    // compare (e.g. CarCare), and internal switches that gate backend behaviour. Only the former
    // belong in this table, so anything flagged hidden is dropped.
    //
    // Excluded by identifier rather than per entry: if a strategy omits the flag while another sets
    // it, the identifier would otherwise reappear here and render as "Not Included" on that card.
    const hiddenIdentifiers = new Set(
      feeStrategies.flatMap((strategy) =>
        strategy.features
          .filter((feature) => feature.hidden)
          .map((feature) => feature.identifier)
      )
    );

    return distinctValues(
      feeStrategies.flatMap((strategy) =>
        strategy.features
          .filter((feature) => !hiddenIdentifiers.has(feature.identifier))
          .map((feature) => feature.identifier)
      )
    );
  }, [feeStrategies]);

  return (
    <div
      data-testid="fee-strategy-picker"
      className={styles.feeStrategyPickerContainer}
    >
      <div className={styles.headerSection}>
        <div>
          <h2 className={styles.title}>Manage fee strategy</h2>
          <p className={styles.subtitle}>
            Fee strategy setup allowed only at policy bind or renewal—guide
            customer accordingly.
          </p>
        </div>
        {onLearnMore && (
          <button
            className={styles.learnMoreButton}
            onClick={onLearnMore}
            data-testid="learn-more-button"
          >
            Learn more
          </button>
        )}
      </div>
      <div className={styles.feeStrategyCardGroup}>
        {feeStrategies.map((strategy) => {
          const isSelected = strategy.name === selectedStrategy;

          return (
            <FeeStrategyCard
              strategy={strategy}
              selected={isSelected}
              allFees={allFees}
              allFeatures={allFeatures}
              onSelectStrategy={() => onStrategySelect(strategy.name)}
              disabled={disabled}
              key={strategy.name}
            />
          );
        })}
      </div>
    </div>
  );
};
